/**
 * The demo timeline: the ONE clock every visible quantity in a landing demo
 * reads. There is no other time source in a demo. Three consumers, and all
 * three must take their value from here or seams appear at the poster swap:
 *
 *   1. the scene render (waves, bob, spin, drip) - `frameStart` each frame;
 *   2. the graph expression clock (the material's animated `t` expressions) -
 *      the demo frame call applies `frameStart` to it;
 *   3. pointer events entering the simulation - `now`.
 *
 * Lifecycle (`phase`):
 *   held    - before the poster swap has landed on glass. Time is exactly 0.
 *             The frame loop renders one frame (pixel-identical to the
 *             poster, which is itself an engine capture at t = 0) and then
 *             draws nothing until release.
 *   ramping - after `requestStart`. The first frame renders at exactly t = 0
 *             again (the origin is stamped at that frame's END, so time
 *             advances only from the second frame), then time follows
 *             `demoTimeWarp`: a long visually-still head easing into motion.
 *   steady  - unit-rate time from the end of the ramp onward.
 *
 * Rule: raw performance.now() / Date.now() values never feed an animation or
 * simulation path - they exist here only as deltas against the origin.
 * Telemetry and stage timing are the only legitimate raw-clock uses.
 */

/** The wake-up ramp duration. 0 disables the ramp entirely. */
export const DEMO_RAMP_MS = 400;

/** Wake-up time warp: effective time integrates a smoothstep velocity
 *  v(x) = 3x^2 - 2x^3: zero velocity at t = 0 (motion continues the frozen
 *  poster pose with no discontinuity) and exact unit speed at DEMO_RAMP_MS.
 *  Steady time is raw time minus the T/2 the ramp absorbed. */
export function demoTimeWarp(rawMs: number): number {
	if (rawMs >= DEMO_RAMP_MS) return rawMs - DEMO_RAMP_MS / 2;
	const x = rawMs / DEMO_RAMP_MS;
	return DEMO_RAMP_MS * x * x * x * (1 - x / 2);
}

export type DemoClockPhase = 'held' | 'ramping' | 'steady';

export class DemoClock {
	private origin: number | null = null;
	private startRequested = false;
	private pendingOriginStamp = false;

	get phase(): DemoClockPhase {
		// A requested start is already 'ramping' even before the origin is
		// stamped: the origin comes from the first ramping frame's end, so
		// treating this window as 'held' would block that frame forever.
		if (this.origin === null) return this.startRequested ? 'ramping' : 'held';
		return performance.now() - this.origin >= DEMO_RAMP_MS ? 'steady' : 'ramping';
	}

	/** The page confirmed the poster swap composited; time may begin. The
	 *  origin is stamped by the NEXT frame's `frameEnd`, so that frame is
	 *  exactly t = 0. */
	requestStart() {
		this.startRequested = true;
	}

	/** Demo time for a frame about to render. Held (and the first
	 *  post-release frame) is exactly 0. */
	frameStart(nowMs: number): number {
		if (this.origin === null) {
			if (this.startRequested) this.pendingOriginStamp = true;
			return 0;
		}
		return demoTimeWarp(nowMs - this.origin);
	}

	/** Stamps the origin at the end of the first post-release frame: time
	 *  advances only from the frame after it. */
	frameEnd(nowMs: number) {
		if (this.pendingOriginStamp) {
			this.pendingOriginStamp = false;
			this.origin = nowMs;
		}
	}

	/** Demo time for out-of-frame consumers (pointer events). Identical
	 *  scale to `frameStart`: held is 0, after release the same warp. */
	now(nowMs: number): number {
		return this.origin === null ? 0 : demoTimeWarp(nowMs - this.origin);
	}

	/** Introspection for timeDebug: the full demo-side time picture. */
	debug(nowMs: number): { phase: DemoClockPhase; rawMs: number | null; warpedMs: number } {
		return {
			phase: this.phase,
			rawMs: this.origin === null ? null : +(nowMs - this.origin).toFixed(1),
			warpedMs: +this.now(nowMs).toFixed(1)
		};
	}
}
